import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, RefreshCw, WifiOff, AlertCircle, ChevronLeft, ChevronRight, FileImage } from 'lucide-react';
import { flyerLeadsApi } from '@/lib/api';
import type { ProgramFlyerLead } from '@/types/program-flyer-lead';
import { formatAdminBusinessEt } from '@/lib/admin-datetime';

interface FlyerLeadListProps {
  onSelect: (leadId: string) => void;
}

export const FlyerLeadList: React.FC<FlyerLeadListProps> = ({ onSelect }) => {
  const [leads, setLeads] = useState<ProgramFlyerLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const load = useCallback(
    async (showRefresh = false) => {
      try {
        setError(null);
        if (showRefresh) setRefreshing(true);
        else setLoading(true);

        const res = await flyerLeadsApi.list({
          page,
          limit,
          sortBy: 'createdAt',
          sortOrder: 'desc',
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        });

        if (res.statusCode !== 200) {
          throw new Error((res as { message?: string }).message || 'Failed to load flyer leads');
        }
        setLeads((res.data.leads || []) as ProgramFlyerLead[]);
        setTotal(res.data.pagination?.total ?? 0);
      } catch (e) {
        console.error(e);
        setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [page, limit, debouncedSearch]
  );

  useEffect(() => {
    if (isOnline) void load();
  }, [load, isOnline]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const formatShort = (iso?: string | null) => {
    if (!iso) return '—';
    return formatAdminBusinessEt(iso);
  };

  if (error) {
    return (
      <Card className="p-8 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold mb-2">Failed to load flyer leads</h3>
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button onClick={() => void load()} className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Try again
        </Button>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {!isOnline && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded text-sm flex items-center gap-2">
          <WifiOff className="h-4 w-4 shrink-0" />
          You&apos;re offline.
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileImage className="h-5 w-5" />
              Flyer leads
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => void load(true)} disabled={refreshing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search email, name, phone, program…"
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPage(1);
              }}
            />
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Lead</TableHead>
                  <TableHead>Program</TableHead>
                  <TableHead>Downloads</TableHead>
                  <TableHead className="hidden md:table-cell">Last download</TableHead>
                  <TableHead className="w-[100px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-12">
                      <FileImage className="h-10 w-10 mx-auto mb-2 opacity-40" />
                      No flyer leads match your filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  leads.map((lead) => (
                    <TableRow
                      key={lead.leadId}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => onSelect(lead.leadId)}
                    >
                      <TableCell>
                        <div className="font-mono text-xs text-muted-foreground">{lead.leadId}</div>
                        <div className="font-medium text-sm">{lead.fullName}</div>
                        <div className="text-xs text-muted-foreground break-all">{lead.email}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-medium">{lead.programTitle}</div>
                        <Badge variant="outline" className="mt-1 capitalize text-xs">
                          {lead.programType.replace(/-/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="font-semibold">{lead.downloadCount}</span>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                        {formatShort(lead.lastDownloadedAt)}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelect(lead.leadId);
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages} ({total} total)
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
